import React, { useState } from 'react';
import { Mail, Phone, Linkedin, Github, Send, MapPin } from 'lucide-react';
import { personalInfo } from '../data/mock';

const SCRIPT_URL =
  "https://script.google.com/macros/s/AKfycby39gHECvw0VHCCfDp22M81wz8wMJ-I7N7M72fX5Z61pnOHHKOE39BF8gFuP_P1cJaL/exec";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('email', formData.email);
      data.append('message', formData.message);

      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        body: data,
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });

      // Reset submitted state after 3 seconds
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again, or email me directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="mb-16">
          <span className="label-text text-[rgba(35,35,35,0.5)] mb-4 block">Get In Touch</span>
          <h2 className="section-title">Contact</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <p className="font-body text-lg lg:text-xl text-[rgba(35,35,35,0.8)] leading-relaxed mb-10">
              I'm always interested in hearing about new opportunities, projects, and collaborations. Feel free to reach out!
            </p>

            <div className="space-y-6">
              {/* Email */}
              <a
                href={`mailto:${personalInfo.email}`}
                className="flex items-center gap-4 group"
              >
                <div className="p-3 bg-[#38FF62] group-hover:bg-[#232323] transition-colors">
                  <Mail className="w-5 h-5 text-[#232323] group-hover:text-[#38FF62] transition-colors" />
                </div>
                <div>
                  <span className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block">Email</span>
                  <span className="font-body text-[#232323] group-hover:text-[#38FF62] transition-colors">
                    {personalInfo.email}
                  </span>
                </div>
              </a>

              {/* Phone */}
              <a
                href={`tel:${personalInfo.phone}`}
                className="flex items-center gap-4 group"
              >
                <div className="p-3 bg-[#38FF62] group-hover:bg-[#232323] transition-colors">
                  <Phone className="w-5 h-5 text-[#232323] group-hover:text-[#38FF62] transition-colors" />
                </div>
                <div>
                  <span className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block">Phone</span>
                  <span className="font-body text-[#232323] group-hover:text-[#38FF62] transition-colors">
                    {personalInfo.phone}
                  </span>
                </div>
              </a>

              {/* Location */}
              <div className="flex items-center gap-4">
                <div className="p-3 bg-[rgba(35,35,35,0.1)]">
                  <MapPin className="w-5 h-5 text-[#232323]" />
                </div>
                <div>
                  <span className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block">Location</span>
                  <span className="font-body text-[#232323]">
                    {personalInfo.location}
                  </span>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex gap-4 mt-10">
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 border border-[rgba(35,35,35,0.2)] hover:bg-[#232323] hover:border-[#232323] group transition-all"
              >
                <Linkedin className="w-5 h-5 group-hover:text-[#38FF62] transition-colors" />
              </a>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                className="p-4 border border-[rgba(35,35,35,0.2)] hover:bg-[#232323] hover:border-[#232323] group transition-all"
              >
                <Github className="w-5 h-5 group-hover:text-[#38FF62] transition-colors" />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="card-sharp p-8 lg:p-10">
            <h3 className="font-display text-xl font-semibold mb-6">Send a Message</h3>
            
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#38FF62] mx-auto mb-4 flex items-center justify-center">
                  <Send className="w-8 h-8 text-[#232323]" />
                </div>
                <h4 className="font-display text-xl font-semibold mb-2">Message Sent!</h4>
                <p className="font-body text-[rgba(35,35,35,0.7)]">
                  Thank you for reaching out. I'll get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#F2F2F2] border-none font-body focus:outline-none focus:ring-2 focus:ring-[#38FF62]"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#F2F2F2] border-none font-body focus:outline-none focus:ring-2 focus:ring-[#38FF62]"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="label-text text-[10px] text-[rgba(35,35,35,0.5)] block mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-[#F2F2F2] border-none font-body focus:outline-none focus:ring-2 focus:ring-[#38FF62] resize-none"
                    placeholder="Tell me about your project or opportunity..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-accent flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                {error && (
                  <p className="text-sm text-red-600 text-center">
                    {error}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;